import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth';
import { formatPrice } from '@/lib/utils';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilExamDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const navigate = useNavigate();
  

  const { data: exam, isLoading } = useQuery({
    queryKey: ['exam-detail', slug],
    queryFn: async () => {
      const res = await fetch(`/api/exams/${slug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
  });

  if (isLoading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: S.font, color: 'var(--soleil-mut)' }}>
      Loading...
    </div>
  );

  if (!exam) return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <p style={{ fontFamily: S.font, color: 'var(--soleil-mut)' }}>Exam not found</p>
      <Link to="/exams" style={{ fontFamily: S.font, color: 'var(--soleil-gold)' }}>← Back to Exams</Link>
    </div>
  );

  

  const handleRegister = () => {
    if (!user) {
      navigate(`/login?redirect=/exams/${slug}`);
      return;
    }
    navigate(`/exam-checkout/${exam.id}`);
  };

  const features = isRTL ? [
    { icon: '🏆', text: 'شهادة معترف بها عالمياً من PeopleCert' },
    { icon: '📋', text: 'امتحان إلكتروني أو ورقي — اختر عند التسجيل' },
    { icon: '⏱️', text: '60 دقيقة — 40 سؤال اختيار من متعدد' },
    { icon: '✅', text: 'درجة النجاح: 65% (26 من 40)' },
    { icon: '📜', text: 'شهادة رقمية فورية عند النجاح' },
    { icon: '🔄', text: 'إمكانية إعادة الامتحان في حال عدم النجاح' },
  ] : [
    { icon: '🏆', text: 'Globally recognized PeopleCert certificate' },
    { icon: '📋', text: 'Online exam — choose computer or paper at registration' },
    { icon: '⏱️', text: '60 minutes — 40 multiple choice questions' },
    { icon: '✅', text: 'Pass mark: 65% (26 out of 40)' },
    { icon: '📜', text: 'Digital certificate issued upon passing' },
    { icon: '🔄', text: 'Retake available if unsuccessful' },
  ];

  return (
    <div style={{ background: 'var(--soleil-cream)', minHeight: '100vh' }}>
      <section style={{ padding: '48px 24px 80px' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <Link to="/exams" style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)', textDecoration: 'none', marginBottom: 32, display: 'inline-block' }}>
            ← {isRTL ? 'العودة للامتحانات' : 'Back to Exams'}
          </Link>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }}>
            {/* Main content */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'start' }}>
              {/* Left - Info */}
              <div>
                <div style={{ display: 'inline-block', fontFamily: S.font, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--soleil-gold-dark)', background: 'var(--soleil-gold-pale)', padding: '5px 12px', borderRadius: 20, marginBottom: 16 }}>
                  {isRTL ? 'شهادة PeopleCert' : 'PEOPLECERT CERTIFIED'}
                </div>
                <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 16 }}>
                  {isRTL ? exam.titleAr : exam.titleEn}
                </h1>
                <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.8, marginBottom: 32 }}>
                  {isRTL ? exam.descriptionAr : exam.descriptionEn}
                </p>

                {/* What's included */}
                <h2 style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 20 }}>
                  {isRTL ? 'ماذا يتضمن' : "What's included"}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: '#fff', borderRadius: 14, border: '1px solid var(--soleil-sand)' }}>
                      <span style={{ fontSize: 20 }}>{f.icon}</span>
                      <span style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-ink)', fontWeight: 500 }}>{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Pricing card */}
              <div style={{ background: '#fff', borderRadius: 20, border: '1px solid var(--soleil-sand)', padding: 28, position: 'sticky', top: 100 }}>
                <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-mut)', letterSpacing: '0.1em', marginBottom: 16 }}>
                  {isRTL ? 'أسعار الامتحان' : 'EXAM PRICING'}
                </div>

                {/* Computer price */}
                <div style={{ padding: '16px 18px', borderRadius: 14, border: '1px solid var(--soleil-sand)', background: '#fff', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontFamily: S.font, fontSize: 14, fontWeight: 700, color: 'var(--soleil-ink)', marginBottom: 3 }}>
                        💻 {isRTL ? 'امتحان إلكتروني' : 'Computer-Based'}
                      </div>
                      <div style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-sub)' }}>
                        {isRTL ? 'عبر الإنترنت من أي مكان' : 'Online from anywhere'}
                      </div>
                    </div>
                    <div style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 700, color: 'var(--soleil-ink)' }}>
                      {exam.computerPrice?.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--soleil-mut)' }}>{exam.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Paper price */}
                {exam.paperPrice != null && (
                <div style={{ padding: '16px 18px', borderRadius: 14, border: '1px solid var(--soleil-sand)', background: '#fff', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontFamily: S.font, fontSize: 14, fontWeight: 700, color: 'var(--soleil-ink)', marginBottom: 3 }}>
                        📝 {isRTL ? 'امتحان ورقي' : 'Paper-Based'}
                      </div>
                      <div style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-sub)' }}>
                        {isRTL ? 'حضوري في بغداد' : 'In-person in Baghdad'}
                      </div>
                    </div>
                    <div style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 700, color: 'var(--soleil-ink)' }}>
                      {exam.paperPrice?.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--soleil-mut)' }}>{exam.currency}</span>
                    </div>
                  </div>
                </div>
                )}

                {/* Register button */}
                <div style={{ paddingTop: 14, borderTop: '1px solid var(--soleil-sand)', marginTop: 14 }}>
                <button onClick={handleRegister}
                  style={{ width: '100%', fontFamily: S.font, fontSize: 15, fontWeight: 700, color: '#fff', background: 'var(--soleil-gold)', padding: '14px 0', borderRadius: 50, border: 'none', cursor: 'pointer', letterSpacing: '0.02em', transition: 'opacity 0.2s' }}
                  onMouseEnter={e => (e.target as HTMLElement).style.opacity = '0.9'}
                  onMouseLeave={e => (e.target as HTMLElement).style.opacity = '1'}
                >
                  {isRTL ? 'سجّل الآن' : 'Register Now →'}
                </button>
                </div>

                {!user && (
                  <p style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)', textAlign: 'center', marginTop: 12 }}>
                    {isRTL ? 'يجب تسجيل الدخول للمتابعة' : 'You need to sign in to continue'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsive override */}
      <style>{`
        @media (max-width: 768px) {
          [style*="grid-template-columns: 1fr 380px"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
